import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { services } from '@/components/services/servicesData';
import { useLang } from '@/lib/LanguageContext';

// Warm the service page chunk so clicks open instantly (same chunk the router lazy-loads).
const preload = () => import('@/pages/ServiceDetail');

export default function ServicesStrip({ eyebrow, title, items }) {
  const { t } = useLang();
  const all = services.map(({ icon, title: name, tag, slug }) => ({ icon, title: t(name), tag: t(tag), to: `/services/${slug}` }));
  // Country pages pass their localized services; the rest of the catalog is appended so every country shows all services.
  const list = items ? [...items, ...all.filter((s) => !items.some((i) => i.ae === s.to))] : all;
  useEffect(() => {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    idle(preload);
  }, []);

  return (
    <section id="services" className="relative py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-xs font-inter font-medium tracking-[0.3em] uppercase text-primary mb-4 block">{eyebrow || t('What We Build for Clients')}</span>
          <h2 className="font-inter font-black text-4xl md:text-5xl tracking-[-0.03em] text-foreground mb-4">{title || t('Services for Businesses in Dubai & the UAE')}</h2>
          <div className="w-16 h-px bg-primary/40 mx-auto" />
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map(({ icon: Icon, title, tag, to }) => (
            <li key={to}>
              <Link
                to={to}
                onMouseEnter={preload}
                onFocus={preload}
                onTouchStart={preload}
                className="group flex items-center gap-4 h-full rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm p-5 hover:border-primary/30 transition-all duration-500 hover:shadow-xl hover:shadow-primary/5">
                <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                  {Icon && <Icon className="w-5 h-5 text-primary" />}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-inter font-bold text-foreground group-hover:text-primary transition-colors duration-300">{title}</h3>
                  <p className="text-[10px] font-inter font-medium tracking-[0.15em] uppercase text-muted-foreground">{tag}</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
        <Link to="/prices" className="inline-block mt-6 text-sm font-semibold text-primary hover:underline">See packages and pricing →</Link>
      </div>
    </section>
  );
}